import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { 
    Calculator, Save, Loader2, Euro, Building2, Users, 
    Crown, Zap, HardHat, Info, CheckCircle2, Percent, Edit3, Lock, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { prospectsAPI, teamsV2API } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

/**
 * CommissionUnifiee - Encart unique pour gérer les commissions
 * 
 * FONCTIONNEMENT:
 * ===============
 * 1. Full Admin saisit la commission totale HT
 * 2. Les parts sont calculées automatiquement selon les règles:
 *    - Si équipe: utilise les % de l'équipe
 *    - Si VP seul ou admin: utilise les % individuels
 *    - Si Apporteur "électron libre" (sans VP): Apporteur prend son % sur le total
 * 3. Possibilité de modifier chaque part individuellement
 * 4. Validation finale qui enregistre tout
 */

export function CommissionUnifiee({ prospect, onUpdate, pipelineType = 'easy_power' }) {
    const { user } = useAuth();
    const [saving, setSaving] = useState(false);
    const [teamInfo, setTeamInfo] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [manualMode, setManualMode] = useState(false);
    
    // Permissions
    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = (user?.role === 'admin' || user?.is_admin === true);
    const isVP = user?.role === 'vp';
    const isApporteur = user?.role === 'apporteur';
    const isComptable = user?.role === 'comptable';
    
    // Vérifier si l'utilisateur est le VP Superviseur de ce dossier
    const isVpSuperviseurDuDossier = isVP && prospect?.dossier_vp_2_id === user?.id;
    
    // Vérifier si un VP est assigné au dossier
    const hasVP = !!prospect?.dossier_vp_id;
    
    // Vérifier si un apporteur est assigné au dossier
    const hasApporteur = !!prospect?.dossier_apporteur_id;
    
    // CAS PARTICULIER: Apporteur "électron libre" = apporteur SANS VP sur le dossier
    const isApporteurElectronLibre = hasApporteur && !hasVP;
    
    // Permissions d'édition et de visualisation
    const canEdit = isAdminFull || isAdmin;
    // VP Superviseur peut voir toutes les parts (comme Full Admin) pour son équipe
    // COMPTABLE: Ne voit PAS tout, seulement la commission de l'utilisateur assigné
    const canSeeAll = isAdminFull || isAdmin;
    const canSeeVpParts = canSeeAll || isVP;
    
    // Dossier gelé ?
    const isFrozen = prospect?.status === 'cloture' || prospect?.status === 'Clôturé';
    
    // Infos du dossier
    const isChantier = pipelineType === 'chantier';
    const pipelineLabel = isChantier ? 'Chantier' : 'Easy Power';
    const PipelineIcon = isChantier ? HardHat : Zap;
    
    // Commission totale selon le pipeline
    const commissionKey = isChantier ? 'commission_totale_ch' : 'commission_totale_ep';
    const commissionHtKey = isChantier ? 'commission_ht_ch' : 'commission_ht_ep';

    // Champs de % selon le pipeline : le CHANTIER a ses propres champs (_ch), JAMAIS ceux d'Easy Power
    const vpPctField = isChantier ? 'vp_commission_percent_ch' : 'vp_commission_percent';
    const appPctField = isChantier ? 'apporteur_commission_percent_ch' : 'apporteur_commission_percent';
    const defaultVpPct = isChantier ? 15 : 21;

    // Override manuel admin (Easy Power uniquement depuis cet encart ; le Chantier se gère par chantier)
    const prefix = isChantier ? 'ch' : 'ep';
    const isManualOverride = !!prospect?.[`${prefix}_commission_manual_override`];
    const canManualOverride = isAdminFull && !isChantier;
    const storedParts = {
        valnexo: prospect?.[`${prefix}_restant_valnexo`],
        atService: prospect?.[`${prefix}_restant_at_service`],
        vp: prospect?.[`${prefix}_commission_vp_payeur_nette`],
        apporteur: prospect?.[`${prefix}_commission_apporteur_1`] ?? prospect?.[`${prefix}_commission_apporteur`],
    };
    
    // État du formulaire - Si pas d'apporteur, forcer % apporteur à 0
    const [formData, setFormData] = useState({
        commissionTotale: prospect?.[commissionKey] || prospect?.[commissionHtKey] || '',
        vpPercent: prospect?.[vpPctField] ?? defaultVpPct,
        vp2Percent: prospect?.vp_2_commission_percent ?? 9, // VP Superviseur
        apporteurPercent: hasApporteur ? (prospect?.[appPctField] ?? 50) : 0, // 0 si pas d'apporteur
        // Parts modifiables individuellement
        partValnexo: null,
        partATService: null,
        partVP: null,
        partVP2: null, // VP Superviseur
        partApporteur: null
    });
    
    // Charger les infos de l'équipe
    const loadTeamInfo = useCallback(async () => {
        // Trouver l'équipe basée sur le VP du dossier
        const vpId = prospect?.dossier_vp_id;
        if (!vpId) return;
        
        try {
            // Chercher l'équipe où ce VP est payeur ou superviseur
            const res = await teamsV2API.getAll();
            const teams = res.data || [];
            const team = teams.find(t => 
                t.is_active && (t.payer_vp_id === vpId || t.supervisor_vp_id === vpId)
            );
            
            if (team) {
                setTeamInfo(team);
                // Mettre à jour les pourcentages selon l'équipe
                if (team.commission_split) {
                    const payerPct = team.commission_split.payer_percent || 70;
                    const supervisorPct = team.commission_split.supervisor_percent || 30;
                    const baseVPTotal = isChantier ? 15 : 30; // 15% chantier / 30% Easy Power
                    const vpPct = Math.round(baseVPTotal * payerPct / 100 * 10) / 10;
                    setFormData(prev => ({
                        ...prev,
                        vpPercent: prospect?.[vpPctField] || vpPct
                    }));
                }
                
                // Chercher le taux de l'apporteur dans l'équipe
                if (prospect?.dossier_apporteur_id && team.member_commissions) {
                    const appCommission = team.member_commissions.find(
                        mc => mc.user_id === prospect.dossier_apporteur_id
                    );
                    if (appCommission) {
                        const rateKey = isChantier ? 'commission_percent_ch' : 'commission_percent_ep';
                        const rate = appCommission[rateKey] || appCommission.commission_percent_ep || 50;
                        setFormData(prev => ({
                            ...prev,
                            apporteurPercent: prospect?.[appPctField] || rate
                        }));
                    }
                }
            }
        } catch (err) {
            console.error('Erreur chargement équipe:', err);
        }
    }, [prospect?.dossier_vp_id, prospect?.dossier_apporteur_id, isChantier]);
    
    useEffect(() => {
        loadTeamInfo();
    }, [loadTeamInfo]);

    // Initialiser le mode manuel selon l'état stocké du dossier
    useEffect(() => {
        setManualMode(isManualOverride);
    }, [isManualOverride]);
    
    // Synchroniser avec le prospect
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            commissionTotale: prospect?.[commissionKey] || prospect?.[commissionHtKey] || '',
            vpPercent: prospect?.[vpPctField] || prev.vpPercent,
            vp2Percent: prospect?.vp_2_commission_percent || prev.vp2Percent, // VP Superviseur
            apporteurPercent: prospect?.[appPctField] || prev.apporteurPercent
        }));
    }, [prospect, commissionKey, commissionHtKey]);
    
    // Vérifier si on a un VP Superviseur sur ce dossier
    const hasVpSuperviseur = !!prospect?.dossier_vp_2_id;
    
    // Calculs automatiques
    const calculations = useMemo(() => {
        const total = parseFloat(formData.commissionTotale) || 0;
        
        // ============== CAS 1: APPORTEUR ÉLECTRON LIBRE (sans VP) ==============
        if (isApporteurElectronLibre) {
            // Apporteur prend son % directement sur le TOTAL (pas sur commission VP)
            const appPct = parseFloat(formData.apporteurPercent) || 0;
            const apporteur = total * (appPct / 100);
            
            // Le reste est partagé 50/50 entre Valnexo et AT Service
            const resteApresApporteur = total - apporteur;
            const valnexo = resteApresApporteur * 0.5;
            const atService = resteApresApporteur * 0.5;
            
            return {
                total,
                vpBrute: 0,
                vpNette: 0,
                vp2: 0,
                apporteur,
                valnexo,
                atService,
                resteApresVPs: resteApresApporteur,
                isElectronLibre: true
            };
        }
        
        // ============== CAS 2: STANDARD (avec VP) ==============
        const vpPct = parseFloat(formData.vpPercent) || 0;
        // VP Superviseur: seulement si un VP2 est assigné au dossier
        const vp2Pct = hasVpSuperviseur ? (parseFloat(formData.vp2Percent) || 0) : 0;
        // Apporteur: seulement si un apporteur est assigné au dossier
        const appPct = hasApporteur ? (parseFloat(formData.apporteurPercent) || 0) : 0;
        
        // LOGIQUE avec VP:
        // 1. VP Payeur prend son % sur le total
        const vpBrute = total * (vpPct / 100);
        
        // 2. VP Superviseur prend son % sur le total (SEULEMENT s'il est assigné)
        const vp2 = hasVpSuperviseur ? (total * (vp2Pct / 100)) : 0;
        
        // 3. Apporteur prend son % sur la commission VP Payeur brute (SEULEMENT s'il est assigné)
        const apporteur = hasApporteur ? (vpBrute * (appPct / 100)) : 0;
        
        // 4. VP Payeur nette = brute - apporteur
        const vpNette = vpBrute - apporteur;
        
        // 5. Le RESTE (après VP Payeur ET VP Superviseur) est partagé 50/50 entre Valnexo et AT Service
        const resteApresVPs = total - vpBrute - vp2;
        const valnexo = resteApresVPs * 0.5;
        const atService = resteApresVPs * 0.5;
        
        return {
            total,
            vpBrute,
            vpNette,
            vp2, // Commission VP Superviseur
            apporteur,
            valnexo,
            atService,
            resteApresVPs,
            isElectronLibre: false
        };
    }, [formData.commissionTotale, formData.vpPercent, formData.vp2Percent, formData.apporteurPercent, hasVpSuperviseur, hasApporteur, isApporteurElectronLibre]);
    
    // Parts affichées (modifiées, stockées si override, ou calculées)
    const displayParts = useMemo(() => {
        // Base = valeurs stockées si le dossier est en override manuel, sinon calcul auto
        const baseVp = (canManualOverride && isManualOverride && storedParts.vp != null) ? storedParts.vp : calculations.vpNette;
        const baseApp = (canManualOverride && isManualOverride && storedParts.apporteur != null) ? storedParts.apporteur : calculations.apporteur;
        const baseValnexo = (canManualOverride && isManualOverride && storedParts.valnexo != null) ? storedParts.valnexo : calculations.valnexo;

        const vp = formData.partVP !== null ? formData.partVP : baseVp;
        const apporteur = formData.partApporteur !== null ? formData.partApporteur : baseApp;
        const valnexo = formData.partValnexo !== null ? formData.partValnexo : baseValnexo;
        const vp2 = formData.partVP2 !== null ? formData.partVP2 : calculations.vp2;

        // En mode manuel : AT Service = solde (total - VP - Apporteur - VP2 - Valnexo)
        let atService;
        if (canManualOverride && manualMode) {
            atService = Math.round((calculations.total - vp - apporteur - vp2 - valnexo) * 100) / 100;
        } else {
            atService = formData.partATService !== null ? formData.partATService
                : ((isManualOverride && storedParts.atService != null) ? storedParts.atService : calculations.atService);
        }
        return { valnexo, atService, vp, vp2, apporteur };
    }, [formData, calculations, canManualOverride, manualMode, isManualOverride, storedParts.vp, storedParts.apporteur, storedParts.valnexo, storedParts.atService]);

    // Dépassement du total en mode manuel ?
    const manualOverBudget = canManualOverride && manualMode && displayParts.atService < -0.01;
    
    // Fonction pour récupérer LA commission de l'apporteur connecté (UNIQUEMENT s'il est assigné)
    // UTILISE LA VALEUR STOCKÉE EN BASE (ep_commission_apporteur) qui est déjà calculée correctement par le backend
    const getMyApporteurCommission = () => {
        if (!isApporteur || !user?.id) return 0;
        
        // Vérifier si l'utilisateur est apporteur sur CE dossier
        const isApporteur1 = prospect?.dossier_apporteur_id === user.id;
        const isApporteur2 = prospect?.dossier_apporteur_2_id === user.id;
        
        if (!isApporteur1 && !isApporteur2) {
            // L'apporteur n'est pas sur ce dossier
            return 0;
        }
        
        // Pour l'apporteur, utiliser la valeur STOCKÉE EN BASE (calculée par le backend)
        // C'est la valeur correcte qui correspond à ce que le Full Admin voit
        if (isChantier) {
            if (isApporteur1) {
                return prospect?.ch_commission_apporteur_1 || prospect?.ch_commission_apporteur || 0;
            } else {
                return prospect?.ch_commission_apporteur_2 || 0;
            }
        } else {
            if (isApporteur1) {
                return prospect?.ep_commission_apporteur_1 || prospect?.ep_commission_apporteur || 0;
            } else {
                return prospect?.ep_commission_apporteur_2 || 0;
            }
        }
    };
    
    // Sauvegarder les commissions
    const handleSave = async () => {
        if (!formData.commissionTotale || parseFloat(formData.commissionTotale) <= 0) {
            toast.error('Veuillez saisir une commission totale valide');
            return;
        }

        // ===== MODE MANUEL (Easy Power, admin_full) : override persistant =====
        if (canManualOverride && manualMode) {
            if (manualOverBudget) {
                toast.error('La somme VP + Apporteur + Valnexo dépasse la commission totale');
                return;
            }
            setSaving(true);
            try {
                await prospectsAPI.setCommissionOverride(prospect.id, {
                    vp: parseFloat(displayParts.vp) || 0,
                    apporteur: parseFloat(displayParts.apporteur) || 0,
                    valnexo: parseFloat(displayParts.valnexo) || 0,
                });
                toast.success('Répartition manuelle enregistrée et verrouillée');
                setEditMode(false);
                if (onUpdate) onUpdate();
            } catch (err) {
                toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
            } finally {
                setSaving(false);
            }
            return;
        }

        setSaving(true);
        try {
            // IMPORTANT: Utiliser commission_ht_ep/ch pour déclencher le recalcul backend
            const updateData = {
                [commissionHtKey]: parseFloat(formData.commissionTotale),  // commission_ht_ep ou commission_ht_ch
                [commissionKey]: parseFloat(formData.commissionTotale),    // commission_totale_ep ou commission_totale_ch (pour compatibilité)
                [vpPctField]: parseFloat(formData.vpPercent),
                vp_2_commission_percent: parseFloat(formData.vp2Percent), // VP Superviseur
                [appPctField]: parseFloat(formData.apporteurPercent)
            };
            // Si on était en override manuel et qu'on repasse en auto, débloquer le recalcul
            if (canManualOverride && isManualOverride) {
                updateData[`${prefix}_commission_manual_override`] = false;
            }
            
            // Ajouter les parts calculées pour le snapshot
            const snapshotKey = isChantier ? 'commission_snapshot_ch' : 'commission_snapshot_ep';
            updateData[snapshotKey] = {
                commission_totale: parseFloat(formData.commissionTotale),
                vp_percent: parseFloat(formData.vpPercent),
                apporteur_percent: parseFloat(formData.apporteurPercent),
                part_valnexo: displayParts.valnexo,
                part_at_service: displayParts.atService,
                part_vp: displayParts.vp,
                part_apporteur: displayParts.apporteur,
                calculated_at: new Date().toISOString()
            };
            
            await prospectsAPI.update(prospect.id, updateData);
            toast.success('Commissions enregistrées');
            setEditMode(false);
            if (onUpdate) onUpdate();
        } catch (err) {
            console.error('Erreur sauvegarde:', err);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    // Revenir au calcul automatique (Easy Power)
    const handleResetToAuto = async () => {
        setSaving(true);
        try {
            await prospectsAPI.resetCommissionOverride(prospect.id);
            toast.success('Retour au calcul automatique');
            setManualMode(false);
            setFormData(prev => ({ ...prev, partValnexo: null, partATService: null, partVP: null, partVP2: null, partApporteur: null }));
            setEditMode(false);
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };
    
    // Format monétaire
    const formatEuro = (val) => {
        const num = parseFloat(val) || 0;
        return new Intl.NumberFormat('fr-FR', { 
            style: 'currency', 
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(num);
    };
    
    // Calcul du pourcentage
    const getPercent = (part) => {
        if (!calculations.total) return 0;
        return Math.round((part / calculations.total) * 1000) / 10;
    };
    
    // Pour les VPs: détecter si des commissions existent via les valeurs stockées en base
    const getMyVpCommission = () => {
        if (!isVP || !user?.id) return 0;
        const prefix = isChantier ? 'ch' : 'ep';
        if (prospect?.dossier_vp_id === user.id) {
            return prospect?.[`${prefix}_commission_vp_payeur_nette`] || prospect?.[`${prefix}_commission_vp`] || 0;
        }
        if (prospect?.dossier_vp_2_id === user.id) {
            return prospect?.[`${prefix}_commission_vp_superviseur`] || 0;
        }
        return 0;
    };
    
    const getVpApporteurCommission = () => {
        if (!isVP) return 0;
        const prefix = isChantier ? 'ch' : 'ep';
        return prospect?.[`${prefix}_commission_apporteur_1`] || prospect?.[`${prefix}_commission_apporteur`] || 0;
    };
    
    const vpHasCommission = isVP && (getMyVpCommission() > 0 || getVpApporteurCommission() > 0);
    
    return (
        <Card className={cn(
            "border-2",
            isChantier ? "border-orange-200 bg-orange-50/30" : "border-yellow-200 bg-yellow-50/30"
        )}>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className={cn(
                            "p-2 rounded-lg",
                            isChantier ? "bg-orange-100" : "bg-yellow-100"
                        )}>
                            <PipelineIcon className={cn(
                                "w-5 h-5",
                                isChantier ? "text-orange-600" : "text-yellow-600"
                            )} />
                        </div>
                        <div>
                            <CardTitle className="text-lg flex items-center gap-2">
                                Commission {pipelineLabel}
                                {isFrozen && (
                                    <Badge variant="secondary" className="text-xs">
                                        Gelé
                                    </Badge>
                                )}
                                {isManualOverride && (
                                    <Badge className="text-xs bg-amber-100 text-amber-700 border-amber-200 flex items-center gap-1" data-testid="ep-manual-override-badge">
                                        <Lock className="w-3 h-3" />
                                        Manuel
                                    </Badge>
                                )}
                            </CardTitle>
                            {teamInfo && (
                                <p className="text-sm text-slate-500 flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {teamInfo.name}
                                </p>
                            )}
                        </div>
                    </div>
                    
                    {canEdit && !isFrozen && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditMode(!editMode)}
                        >
                            <Edit3 className="w-4 h-4 mr-1" />
                            {editMode ? 'Annuler' : 'Modifier'}
                        </Button>
                    )}
                </div>
            </CardHeader>
            
            <CardContent className="space-y-4">
                {/* Saisie commission totale - ADMIN UNIQUEMENT (pas VP, pas Apporteur) */}
                {canSeeAll && (
                    <div className="space-y-2">
                        <Label className="flex items-center gap-2 text-sm font-medium">
                            <Euro className="w-4 h-4" />
                            Commission Totale HT
                        </Label>
                        <div className="flex items-center gap-2">
                            <Input
                                type="number"
                                value={formData.commissionTotale}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    commissionTotale: e.target.value,
                                    // Reset les parts manuelles quand on change le total
                                    partValnexo: null,
                                    partATService: null,
                                    partVP: null,
                                    partApporteur: null
                                }))}
                                disabled={!canEdit || isFrozen}
                                placeholder="0"
                                className="text-lg font-semibold"
                                data-testid={`commission-total-${pipelineType}`}
                            />
                            <span className="text-slate-500">€</span>
                        </div>
                    </div>
                )}

                {/* Toggle Répartition manuelle (admin_full, Easy Power) */}
                {canManualOverride && canEdit && !isFrozen && (
                    <div className="flex items-center justify-between gap-3 p-3 bg-amber-50/60 border border-amber-200 rounded-lg">
                        <div className="flex items-center gap-2">
                            <Lock className="w-4 h-4 text-amber-600" />
                            <div>
                                <p className="text-sm font-medium text-amber-800">Répartition manuelle</p>
                                <p className="text-[11px] text-amber-600">Saisir librement VP / Apporteur / Valnexo. AT Service = solde automatique.</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {isManualOverride && (
                                <Button
                                    variant="outline" size="sm"
                                    className="h-8 text-[11px] text-amber-700 border-amber-300 hover:bg-amber-100"
                                    onClick={handleResetToAuto}
                                    disabled={saving}
                                    data-testid="ep-reset-auto-btn"
                                >
                                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                                    Calcul auto
                                </Button>
                            )}
                            <Switch
                                checked={manualMode}
                                onCheckedChange={(v) => { setManualMode(v); if (v) setEditMode(true); }}
                                data-testid="ep-manual-mode-switch"
                            />
                        </div>
                    </div>
                )}
                
                {/* Paramètres (VP % et Apporteur %) - Visible uniquement Full Admin - JAMAIS pour Apporteur - masqué en mode manuel */}
                {editMode && canEdit && !isApporteur && !(canManualOverride && manualMode) && (
                    <div className={cn(
                        "gap-4 p-3 bg-slate-50 rounded-lg",
                        isApporteurElectronLibre ? "grid grid-cols-1" : "grid grid-cols-3"
                    )}>
                        {/* VP Payeur - MASQUÉ si apporteur électron libre */}
                        {!isApporteurElectronLibre && (
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">% VP Payeur</Label>
                                <div className="flex items-center gap-1">
                                    <Input
                                        type="number"
                                        value={formData.vpPercent}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            vpPercent: e.target.value
                                        }))}
                                        className="h-8 text-sm"
                                        min="0"
                                        max="100"
                                    />
                                    <Percent className="w-4 h-4 text-slate-400" />
                                </div>
                            </div>
                        )}
                        {/* VP Superviseur - visible uniquement si présent sur le dossier ET pas électron libre */}
                        {hasVpSuperviseur && !isApporteurElectronLibre && (
                            <div className="space-y-1">
                                <Label className="text-xs text-slate-500">% VP Superviseur</Label>
                                <div className="flex items-center gap-1">
                                    <Input
                                        type="number"
                                        value={formData.vp2Percent}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            vp2Percent: e.target.value
                                        }))}
                                        className="h-8 text-sm"
                                        min="0"
                                        max="100"
                                    />
                                    <Percent className="w-4 h-4 text-slate-400" />
                                </div>
                            </div>
                        )}
                        <div className="space-y-1">
                            <Label className="text-xs text-slate-500">
                                % Apporteur {isApporteurElectronLibre ? "(sur Total)" : "(sur VP Payeur)"}
                            </Label>
                            <div className="flex items-center gap-1">
                                <Input
                                    type="number"
                                    value={formData.apporteurPercent}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        apporteurPercent: e.target.value
                                    }))}
                                    className="h-8 text-sm"
                                    min="0"
                                    max="100"
                                />
                                <Percent className="w-4 h-4 text-slate-400" />
                            </div>
                            {isApporteurElectronLibre && (
                                <p className="text-[10px] text-orange-600 mt-1">
                                    ⚡ Apporteur électron libre (sans VP) - payé par Valnexo ou AT Service
                                </p>
                            )}
                        </div>
                    </div>
                )}
                
                {/* Séparateur - masquer pour Apporteur si pas de commission */}
                {!isApporteur && <Separator />}
                
                {/* Répartition calculée */}
                {calculations.total > 0 || (isApporteur && getMyApporteurCommission() > 0) || vpHasCommission ? (
                    <div className="space-y-3">
                        {/* ============== VUE APPORTEUR: SIMPLIFIÉE - UNIQUEMENT SA COMMISSION ============== */}
                        {isApporteur ? (
                            getMyApporteurCommission() > 0 ? (
                                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-emerald-100 rounded-full">
                                            <Euro className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-emerald-700 font-medium">Votre commission</p>
                                            <p className="text-2xl font-bold text-emerald-800" data-testid="apporteur-commission-display">
                                                {formatEuro(getMyApporteurCommission())}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-center">
                                    <p className="text-sm text-slate-500">Aucune commission sur ce dossier</p>
                                </div>
                            )
                        ) : isVP && !canSeeAll && vpHasCommission ? (
                            /* ============== VUE VP: SA COMMISSION + COMMISSION APPORTEUR ============== */
                            <>
                                <h4 className="text-sm font-medium text-slate-600 flex items-center gap-2">
                                    <Calculator className="w-4 h-4" />
                                    Vos commissions
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {getMyVpCommission() > 0 && (
                                        <PartCard
                                            label="Ma commission (VP)"
                                            value={getMyVpCommission()}
                                            percent={null}
                                            color="purple"
                                            icon={Crown}
                                        />
                                    )}
                                    {hasApporteur && getVpApporteurCommission() > 0 && (
                                        <PartCard
                                            label="Commission Apporteur"
                                            value={getVpApporteurCommission()}
                                            percent={null}
                                            color="orange"
                                            icon={Users}
                                        />
                                    )}
                                </div>
                            </>
                        ) : (
                            /* ============== VUE NORMALE: ADMIN/VP/COMPTABLE ============== */
                            <>
                                <h4 className="text-sm font-medium text-slate-600 flex items-center gap-2">
                                    <Calculator className="w-4 h-4" />
                                    Répartition
                                </h4>
                                
                                {/* Détails des parts - VISIBILITÉ SELON RÔLE */}
                                <div className="grid grid-cols-2 gap-2">
                                    {/* Valnexo & AT Service - Admin/Comptable uniquement */}
                                    {canSeeAll && (
                                        <>
                                            <PartCard
                                                label="Valnexo"
                                                value={displayParts.valnexo}
                                                percent={getPercent(displayParts.valnexo)}
                                                color="blue"
                                                icon={Building2}
                                                editable={editMode && canEdit}
                                                onEdit={(val) => setFormData(prev => ({ ...prev, partValnexo: parseFloat(val) }))}
                                            />
                                            <PartCard
                                                label="AT Service"
                                                value={displayParts.atService}
                                                percent={getPercent(displayParts.atService)}
                                                color="green"
                                                icon={Building2}
                                                editable={editMode && canEdit && !(canManualOverride && manualMode)}
                                                onEdit={(val) => setFormData(prev => ({ ...prev, partATService: parseFloat(val) }))}
                                            />
                                            {/* VP Superviseur - visible uniquement Admin/Comptable */}
                                            {hasVpSuperviseur && (
                                                <PartCard
                                                    label="VP Superviseur"
                                                    value={displayParts.vp2}
                                                    percent={getPercent(displayParts.vp2)}
                                                    color="amber"
                                                    icon={Crown}
                                                    editable={editMode && canEdit}
                                                    onEdit={(val) => setFormData(prev => ({ ...prev, partVP2: parseFloat(val) }))}
                                                />
                                            )}
                                        </>
                                    )}
                                    
                                    {/* VP Payeur - Visible pour Admin/Comptable de VP/VP (pas Apporteur ni Comptable d'Apporteur) */}
                                    {(canSeeAll || isVP) && !isComptable && (
                                        <PartCard
                                            label={isVP ? "Ma commission (VP)" : "VP Payeur"}
                                            value={displayParts.vp}
                                            percent={canSeeAll ? getPercent(displayParts.vp) : null}
                                            color="purple"
                                            icon={Crown}
                                            editable={editMode && canEdit}
                                            onEdit={(val) => setFormData(prev => ({ ...prev, partVP: parseFloat(val) }))}
                                        />
                                    )}
                                    
                                    {/* Apporteur - Visible pour Admin ET VP (pas Apporteur - il a sa vue dédiée) */}
                                    {(canSeeAll || isVP) && (
                                        <PartCard
                                            label="Apporteur"
                                            value={displayParts.apporteur}
                                            percent={canSeeAll ? getPercent(displayParts.apporteur) : null}
                                            color="orange"
                                            icon={Users}
                                            editable={editMode && canEdit}
                                            onEdit={(val) => setFormData(prev => ({ ...prev, partApporteur: parseFloat(val) }))}
                                        />
                                    )}
                                </div>
                                
                                {/* Info calcul - Admin UNIQUEMENT (pas VP, pas comptable, pas apporteur) */}
                                {isAdminFull && (
                                    <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded flex items-start gap-2">
                                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                        <span>
                                            VP Payeur reçoit {formData.vpPercent}% = {formatEuro(calculations.vpBrute)} (brute).
                                            {hasVpSuperviseur && ` VP Superviseur: ${formData.vp2Percent}% = ${formatEuro(calculations.vp2)}.`}
                                            {' '}L'apporteur prélève {formData.apporteurPercent}% sur VP Payeur = {formatEuro(calculations.apporteur)}.
                                            VP Payeur net: {formatEuro(calculations.vpNette)}.
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-6 text-slate-400">
                        <Calculator className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">{isApporteur ? "Aucune commission enregistrée" : "Saisissez une commission pour voir la répartition"}</p>
                    </div>
                )}
                
                {/* Bouton Enregistrer - JAMAIS pour Apporteur */}
                {canEdit && !isFrozen && calculations.total > 0 && !isApporteur && (
                    <>
                        {manualOverBudget && (
                            <p className="text-xs text-red-600 flex items-center gap-1" data-testid="ep-over-budget-warning">
                                <Info className="w-3 h-3" />
                                La somme VP + Apporteur + Valnexo dépasse la commission totale.
                            </p>
                        )}
                        <Button
                            onClick={handleSave}
                            disabled={saving || manualOverBudget}
                            className="w-full"
                            data-testid={`save-commission-${pipelineType}`}
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <CheckCircle2 className="w-4 h-4 mr-2" />
                            )}
                            {canManualOverride && manualMode ? 'Enregistrer la répartition manuelle' : 'Enregistrer les commissions'}
                        </Button>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

// Sous-composant pour afficher une part
function PartCard({ label, value, percent, color, icon: Icon, subLabel, editable, onEdit }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    
    useEffect(() => {
        setEditValue(value);
    }, [value]);
    
    const colorClasses = {
        blue: 'bg-blue-50 border-blue-200 text-blue-700',
        green: 'bg-green-50 border-green-200 text-green-700',
        purple: 'bg-purple-50 border-purple-200 text-purple-700',
        orange: 'bg-orange-50 border-orange-200 text-orange-700'
    };
    
    const handleConfirm = () => {
        if (onEdit) onEdit(editValue);
        setIsEditing(false);
    };
    
    const formatEuro = (val) => {
        const num = parseFloat(val) || 0;
        return new Intl.NumberFormat('fr-FR', { 
            style: 'currency', 
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(num);
    };
    
    return (
        <div className={cn(
            "p-3 rounded-lg border",
            colorClasses[color]
        )}>
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1">
                    <Icon className="w-3 h-3" />
                    <span className="text-xs font-medium">{label}</span>
                </div>
                {percent !== null && percent !== undefined && (
                    <Badge variant="outline" className="text-[10px] px-1">
                        {percent}%
                    </Badge>
                )}
            </div>
            
            {isEditing && editable ? (
                <div className="flex items-center gap-1">
                    <Input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="h-7 text-sm"
                        autoFocus
                        onBlur={handleConfirm}
                        onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
                    />
                    <span className="text-xs">€</span>
                </div>
            ) : (
                <div 
                    className={cn(
                        "text-lg font-bold",
                        editable && "cursor-pointer hover:opacity-70"
                    )}
                    onClick={() => editable && setIsEditing(true)}
                >
                    {formatEuro(value)}
                </div>
            )}
            
            {subLabel && (
                <p className="text-[10px] opacity-70 mt-1">{subLabel}</p>
            )}
        </div>
    );
}

export default CommissionUnifiee;
